// ============================================================================
// UNIFIED AI ASSISTANT CONTEXT
// Provides shared credits, session, and theming across ALL AI assistants
// Credits are shared per client per month - NOT per module
// Context carries over between modules for seamless conversation history
// ============================================================================

'use client';

import React, { createContext, useContext, useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';

import type { ReactNode } from 'react';
import { usePathname } from 'next/navigation';

import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import { isAppShellRoute } from '@/lib/platformUtils';
import { notify } from '@/lib/notifications';
import { useSemanticContext } from '@/hooks/useSemanticContext';
import { saveChatSession, loadLatestChatSession, loadChatSession, type ChatMessageRecord } from '@/lib/chatSessionPersistence';
import type { AIMessage as ChatAIMessage, AISuggestedAction } from '@/components/shared/AIAssistantChat';

// ============================================================================
// TYPES
// ============================================================================

interface AISession {
  id: string;
  moduleContext: string;
  created_at: string;
  messages: AIMessage[];
  context?: Record<string, unknown>;
}

type AIMessage = ChatAIMessage;

interface AICredits {
  total: number;
  used: number;
  remaining: number;
  resetDate: string;
  tier?: string;
  usageByModule?: Record<string, { count: number; tokens: number }>;
}

interface AIAssistantContextValue {
  // Credits - shared across all modules
  credits: AICredits | null;
  creditsLoading: boolean;
  refreshCredits: () => Promise<void>;
  consumeCredit: (amount?: number) => void;

  // Session - can carry context between modules
  currentSession: AISession | null;
  sessionLoading: boolean;
  initializeSession: (moduleContext: string, context?: Record<string, unknown>) => Promise<AISession | null>;
  createNewSession: (moduleContext: string) => Promise<AISession | null>;

  // Global conversation history (persisted across modules)
  globalMessages: AIMessage[];
  addMessage: (message: AIMessage) => void;
  clearMessages: () => void;

  // Theming - uses client branding
  primaryColor: string;
  gradientFrom: string;
  gradientTo: string;

  // Ready state
  isReady: boolean;
}

// ============================================================================
// ENHANCED HOOK TYPES
// ============================================================================

interface UseAIAssistantOptions {
  moduleKey: string;              // e.g., 'total-finance', 'total-operations'
  moduleContext: string;          // e.g., 'finance', 'operations', 'crm'
  enableSemanticContext?: boolean; // default: true
  enableSemanticIntake?: boolean;  // default: false (only finance for now)
  enableModuleAssistance?: boolean; // default: true — fetch from /api/v1/modules/ai-assistance
  initialPrompt?: string;
  provider?: string;              // 'gemini' | 'openai' | 'total-vision' | etc.
  contextPayload?: Record<string, unknown>; // Module-specific context passed to chat/session API
  sessionScopeKey?: string;      // When this changes, session is re-initialized
  /** Resume a specific MongoDB chat session instead of the latest module
   *  session. Used to carry one assistant thread across surfaces (e.g. the
   *  forms wizard → builder). If the session doesn't exist yet, the id is
   *  pinned so the first save persists under it. */
  resumeSessionId?: string;
}

interface UseAIAssistantReturn extends AIAssistantContextValue {
  // Module-scoped state
  messages: AIMessage[];
  setMessages: React.Dispatch<React.SetStateAction<AIMessage[]>>;
  isGenerating: boolean;
  setIsGenerating: React.Dispatch<React.SetStateAction<boolean>>;
  sessionId: string | null;
  isLoadingHistory: boolean;

  // Actions (extracted from duplicated code across 9 assistants)
  sendMessage: (text: string, files?: File[], options?: { provider?: string }) => Promise<void>;
  newChat: () => void;
  feedback: (messageId: string, type: 'positive' | 'negative') => Promise<void>;
  applyAction: (action: AISuggestedAction) => Promise<void>;
  loadSession: (sessionId: string, sessionDate: string) => Promise<void>;

  // Semantic context (when enabled)
  semanticContext: string;
  playbookContext: string;
  suggestedPrompts: string[];
  hasApiContext: boolean;
}

const defaultCredits: AICredits = {
  total: 500,
  used: 0,
  remaining: 500,
  resetDate: new Date(new Date().getFullYear(), new Date().getMonth() + 1, 1).toISOString()
};

const AIAssistantContext = createContext<AIAssistantContextValue | null>(null);

// ============================================================================
// PROVIDER
// ============================================================================

interface AIAssistantProviderProps {
  children: ReactNode;
}

export function AIAssistantProvider({ children }: AIAssistantProviderProps) {
  // Theme integration
  const { clientBranding } = useTheme();
  
  // Derive colors from client branding
  const primaryColor = clientBranding?.primary_color || '#0d9488';
  const gradientFrom = primaryColor;
  // Create a complementary gradient end color (slightly shifted)
  const gradientTo = shiftColor(primaryColor, 15); // Shift hue by 15 degrees for complementary feel
  
  // Credits state
  const [credits, setCredits] = useState<AICredits | null>(null);
  const [creditsLoading, setCreditsLoading] = useState(true);
  
  // Session state
  const [currentSession, setCurrentSession] = useState<AISession | null>(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  
  // Global message history (persisted across modules)
  const [globalMessages, setGlobalMessages] = useState<AIMessage[]>([]);
  
  // Ready state
  const [isReady, setIsReady] = useState(false);
  
  // Load credits on mount
  const refreshCredits = useCallback(async () => {
    try {
      setCreditsLoading(true);
      // Public pages never need credits — skip the authed call entirely
      // (stale tokens would otherwise produce a 401 on every marketing page).
      if (typeof window !== 'undefined' && !isAppShellRoute(window.location.pathname)) {
        setCredits(defaultCredits);
        return;
      }
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        setCredits(defaultCredits);
        return;
      }
      
      const response = await fetch('/api/v1/ai-assistant/credits', {
        headers: {
          'Authorization': `Bearer ${tokens.access_token}`
        }
      });
      
      const data = await response.json();
      if (data.success && data.credits) {
        setCredits({
          total: data.credits.total,
          used: data.credits.used,
          remaining: data.credits.remaining,
          resetDate: data.credits.resetDate,
          tier: data.credits.tier,
          usageByModule: data.credits.usageByModule
        });
      } else {
        setCredits(defaultCredits);
      }
    } catch (error) {
      console.error('Failed to load AI credits:', error);
      setCredits(defaultCredits);
    } finally {
      setCreditsLoading(false);
      setIsReady(true);
    }
  }, []);
  
  // Consume credits (optimistic update)
  const consumeCredit = useCallback((amount: number = 1) => {
    setCredits(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        used: prev.used + amount,
        remaining: Math.max(0, prev.remaining - amount)
      };
    });
  }, []);
  
  // Initialize session for a module
  const initializeSession = useCallback(async (moduleContext: string, context?: Record<string, unknown>): Promise<AISession | null> => {
    try {
      setSessionLoading(true);
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return null;
      
      const response = await fetch('/api/v1/ai-assistant/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({
          moduleContext,
          context: context || {}
        })
      });
      
      const data = await response.json();
      if (data.success && data.session) {
        const session: AISession = {
          id: data.session.id,
          moduleContext: data.session.moduleContext,
          created_at: data.session.created_at,
          messages: data.session.messages || [],
          context: data.session.context
        };
        setCurrentSession(session);
        
        // Merge session messages with global messages (avoiding duplicates)
        if (session.messages.length > 0) {
          setGlobalMessages(prev => {
            const existingIds = new Set(prev.map(m => m.id));
            const newMessages = session.messages.filter(m => !existingIds.has(m.id));
            return [...prev, ...newMessages];
          });
        }
        
        return session;
      }
      return null;
    } catch (error) {
      console.error('Failed to initialize AI session:', error);
      return null;
    } finally {
      setSessionLoading(false);
    }
  }, []);

  // Create new session (clears current context)
  const createNewSession = useCallback(async (moduleContext: string): Promise<AISession | null> => {
    try {
      setSessionLoading(true);
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return null;
      
      const response = await fetch('/api/v1/ai-assistant/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`
        },
        body: JSON.stringify({ 
          action: 'create_new',
          moduleContext 
        })
      });
      
      const data = await response.json();
      if (data.success && data.session) {
        const session: AISession = {
          id: data.session.id,
          moduleContext: data.session.moduleContext,
          created_at: data.session.created_at,
          messages: []
        };
        setCurrentSession(session);
        return session;
      }
      return null;
    } catch (error) {
      console.error('Failed to create new AI session:', error);
      return null;
    } finally {
      setSessionLoading(false);
    }
  }, []);
  
  // Add message to global history
  const addMessage = useCallback((message: AIMessage) => {
    setGlobalMessages(prev => [...prev, message]);
  }, []);
  
  // Clear all messages
  const clearMessages = useCallback(() => {
    setGlobalMessages([]);
  }, []);
  
  // Load credits on mount
  useEffect(() => {
    refreshCredits();
  }, [refreshCredits]);
  
  const value: AIAssistantContextValue = {
    credits,
    creditsLoading,
    refreshCredits,
    consumeCredit,
    currentSession,
    sessionLoading,
    initializeSession,
    createNewSession,
    globalMessages,
    addMessage,
    clearMessages,
    primaryColor,
    gradientFrom,
    gradientTo,
    isReady
  };
  
  return (
    <AIAssistantContext.Provider value={value}>
      {children}
    </AIAssistantContext.Provider>
  );
}

// ============================================================================
// ENHANCED HOOK
// ============================================================================

export function useAIAssistant(options: UseAIAssistantOptions): UseAIAssistantReturn {
  const context = useContext(AIAssistantContext);
  if (!context) {
    throw new Error('useAIAssistant must be used within an AIAssistantProvider');
  }

  const {
    moduleKey,
    moduleContext,
    enableSemanticContext = true,
    enableSemanticIntake: _enableSemanticIntake = false,
    enableModuleAssistance: _enableModuleAssistance = true,
    initialPrompt: _initialPrompt,
    provider: defaultProvider,
    contextPayload,
  } = options;

  // Keep contextPayload in a ref so sendMessage/loadSession always use the latest without dep churn
  const contextPayloadRef = useRef(contextPayload);
  useEffect(() => {
    contextPayloadRef.current = contextPayload;
  }, [contextPayload]);

  // Module-scoped state
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Track the latest topic for semantic context (updated on each user message)
  const [topic, setTopic] = useState<string>(moduleContext);

  // The assistant is app-shell only — on public pages no assistant UI is
  // mounted, so don't fire its authed session/semantic-context calls.
  const pathname = usePathname();
  const isAppShell = isAppShellRoute(pathname);

  // Semantic context integration
  const semanticContextHook = useSemanticContext({
    moduleKey,
    topic,
    enabled: enableSemanticContext && isAppShell,
  });

  // MongoDB session ID ref — shared between init and auto-save effects
  const mongoSessionIdRef = useRef<string | null>(null);

  // Auto-initialize session on mount or when moduleContext/sessionScopeKey changes
  // Tries MongoDB first (full message array in one document), then falls back
  // to the Postgres-backed session API.
  const initRef = useRef(false);
  const prevScopeKey = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!isAppShell) return;
    const scopeKey = `${options.sessionScopeKey ?? ''}::${options.resumeSessionId ?? ''}`;
    const scopeChanged = prevScopeKey.current !== undefined && prevScopeKey.current !== scopeKey;
    prevScopeKey.current = scopeKey;
    if (initRef.current && !scopeChanged) return;
    initRef.current = true;

    let cancelled = false;
    (async () => {
      // Try MongoDB first — it stores the full conversation in one document.
      // When a resumeSessionId is provided, load that exact thread so the
      // conversation follows the draft across surfaces (wizard → builder).
      const mongoSession = options.resumeSessionId
        ? await loadChatSession(options.resumeSessionId)
        : await loadLatestChatSession(moduleKey || moduleContext, undefined, 'assistant');
      if (!cancelled && mongoSession && mongoSession.messages.length > 0) {
        setMessages(mongoSession.messages as unknown as AIMessage[]);
        mongoSessionIdRef.current = mongoSession.sessionId;
        // Still initialize the Postgres session for credit tracking / history sidebar
        void context.initializeSession(moduleContext, contextPayloadRef.current).then((session) => {
          if (!cancelled && session) setSessionId(session.id);
        });
        return;
      }

      // If a resumeSessionId was provided but no session exists yet, pin the
      // id so the first auto-save persists under it — the other surface
      // sharing the id will then resume this thread. (When a session WAS
      // found, the ref is already set to its id above.)
      if (options.resumeSessionId && !mongoSessionIdRef.current) {
        mongoSessionIdRef.current = options.resumeSessionId;
      }

      // Fall back to Postgres-backed session. When resuming a pinned draft
      // thread that has no history yet, start empty — adopting an unrelated
      // older Postgres session would bleed foreign messages into the draft's
      // conversation.
      const session = await context.initializeSession(moduleContext, contextPayloadRef.current);
      if (!cancelled && session) {
        setSessionId(session.id);
        if (session.messages.length > 0 && !options.resumeSessionId) {
          setMessages(session.messages);
        } else {
          setMessages([]);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [context, moduleContext, moduleKey, isAppShell, options.sessionScopeKey, options.resumeSessionId]);

  // --- sendMessage (extracted from 9 assistant components) ---
  const sendMessage = useCallback(async (
    text: string,
    files?: File[],
    options?: { provider?: string },
  ) => {
    if (!text.trim() || isGenerating) return;

    const userMessage: AIMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };

    setMessages(prev => [...prev, userMessage]);
    setTopic(text); // Update semantic context topic
    setIsGenerating(true);

    try {
      // Prepare file attachments (base64)
      let attachments: { name: string; type: string; size: number; content_base64: string }[] | undefined;
      if (files && files.length > 0) {
        attachments = await Promise.all(
          files.map(file => new Promise<{ name: string; type: string; size: number; content_base64: string }>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64 = (reader.result as string).split(',')[1] || '';
              resolve({ name: file.name, type: file.type, size: file.size, content_base64: base64 });
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          })),
        );
      }

      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        throw new Error('Authentication required');
      }

      // Build semantic context payload if enabled
      const semanticPayload = enableSemanticContext
        ? {
            semanticContext: semanticContextHook.context || '',
            playbookContext: semanticContextHook.playbookContext || '',
          }
        : {};

      const response = await fetch('/api/v1/ai-assistant/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          sessionId,
          message: text,
          moduleContext,
          context: {
            moduleKey,
            ...semanticPayload,
            ...(contextPayloadRef.current || {}),
          },
          ...(options?.provider && { provider: options.provider }),
          ...(!options?.provider && defaultProvider && { provider: defaultProvider }),
          ...(attachments && { attachments }),
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to get AI response';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorMessage;
        } catch {
          if (response.status === 502 || response.status === 504) {
            errorMessage = 'Request timed out. The AI service is taking longer than expected. Please try again.';
          } else if (response.status === 429) {
            errorMessage = 'AI service is busy. Please wait a moment and try again.';
          } else if (response.status === 503) {
            errorMessage = 'AI service is temporarily unavailable. Please try again later.';
          }
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();

      if (data.success) {
        const assistantMessage: AIMessage = {
          id: `msg-${Date.now()}-assistant`,
          role: 'assistant',
          content: data.response,
          timestamp: new Date().toISOString(),
          attachments: data.attachments,
          actions: data.actions,
        };

        setMessages(prev => [...prev, assistantMessage]);
        context.addMessage(userMessage);
        context.addMessage(assistantMessage);

        // Update credits from response
        if (data.credits_remaining !== undefined) {
          context.consumeCredit(1);
        }
      } else {
        throw new Error(data.error || 'Failed to get AI response');
      }
    } catch (error: unknown) {
      console.error('[useAIAssistant] Chat error:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to generate response';
      notify.error(errorMessage);
      setMessages(prev => [...prev, {
        id: `msg-${Date.now()}-error`,
        role: 'assistant',
        content: `Sorry, I encountered an error: ${errorMessage}. Please try again.`,
        timestamp: new Date().toISOString(),
      }]);
    } finally {
      setIsGenerating(false);
    }
  }, [
    isGenerating,
    sessionId,
    moduleContext,
    moduleKey,
    enableSemanticContext,
    semanticContextHook.context,
    semanticContextHook.playbookContext,
    defaultProvider,
    context,
  ]);

  // --- newChat (extracted from 9 assistant components) ---
  const newChat = useCallback(() => {
    setMessages([]);
    setSessionId(null);
    setTopic(moduleContext);
    context.createNewSession(moduleContext).then(session => {
      if (session) {
        setSessionId(session.id);
      }
    }).catch(error => {
      console.error('[useAIAssistant] Failed to create new session:', error);
    });
  }, [context, moduleContext]);

  // --- MongoDB auto-save: persist messages after each exchange ---
  // Debounced so we only save after messages settle (not on every render).
  const mongoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirror of `messages` for the unmount flush below.
  const messagesRef = useRef<AIMessage[]>(messages);
  messagesRef.current = messages;

  const persistChatSession = useCallback((msgs: AIMessage[]) => {
    if (msgs.length === 0) return;
    // Generate a stable session ID if we don't have one yet
    if (!mongoSessionIdRef.current) {
      const today = new Date().toISOString().split('T')[0];
      mongoSessionIdRef.current = `${moduleKey || moduleContext}-${today}-${Date.now()}`;
    }

    const records: ChatMessageRecord[] = msgs.map((m) => ({
      id: m.id,
      role: m.role,
      content: m.content,
      timestamp: m.timestamp,
      actions: m.actions as unknown as Array<Record<string, unknown>>,
      attachments: m.attachments as unknown as Array<Record<string, unknown>>,
      feedback: m.feedback,
    }));

    void saveChatSession({
      sessionId: mongoSessionIdRef.current,
      moduleKey: moduleKey || moduleContext,
      messages: records,
      sessionType: 'assistant',
    });
  }, [moduleKey, moduleContext]);

  useEffect(() => {
    if (messages.length === 0) return;

    // Clear any pending save
    if (mongoSaveTimerRef.current) clearTimeout(mongoSaveTimerRef.current);

    // Debounce save by 2 seconds
    mongoSaveTimerRef.current = setTimeout(() => {
      mongoSaveTimerRef.current = null;
      persistChatSession(messagesRef.current);
    }, 2000);

    return () => {
      if (mongoSaveTimerRef.current) clearTimeout(mongoSaveTimerRef.current);
    };
  }, [messages, persistChatSession]);

  // Flush a pending debounced save synchronously on unmount — otherwise the
  // final exchange is lost when the surface hosting the assistant unmounts
  // (e.g. the wizard's embedded panel when the user opens the builder, which
  // then resumes this thread). useLayoutEffect cleanup runs during the
  // commit, before the next surface's passive init reads the session.
  useLayoutEffect(() => {
    return () => {
      if (mongoSaveTimerRef.current) {
        clearTimeout(mongoSaveTimerRef.current);
        mongoSaveTimerRef.current = null;
        persistChatSession(messagesRef.current);
      }
    };
  }, [persistChatSession]);

  // Reset the MongoDB session ID when a new chat is started. When resuming a
  // shared draft thread, keep the pinned id so the cleared conversation is
  // still saved under the draft's session — the builder then resumes the
  // (fresh) thread rather than the stale pre-reset one.
  useEffect(() => {
    if (sessionId === null && messages.length === 0) {
      mongoSessionIdRef.current = options.resumeSessionId ?? null;
    }
  }, [sessionId, messages.length, options.resumeSessionId]);

  // --- feedback (extracted from 9 assistant components) ---
  const feedback = useCallback(async (messageId: string, type: 'positive' | 'negative') => {
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      await fetch('/api/v1/ai-assistant/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({ messageId, feedback: type, moduleContext }),
      });

      setMessages(prev => prev.map(msg =>
        msg.id === messageId ? { ...msg, feedback: type } : msg,
      ));
    } catch (error) {
      console.error('[useAIAssistant] Failed to submit feedback:', error);
    }
  }, [moduleContext]);

  // --- applyAction (extracted from 9 assistant components) ---
  const applyAction = useCallback(async (action: AISuggestedAction) => {
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) {
        throw new Error('Authentication required');
      }

      const response = await fetch('/api/v1/ai-assistant/apply-action', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          actionId: action.id,
          actionType: action.type,
          actionData: action.data,
          moduleContext,
        }),
      });

      const data = await response.json();
      if (data.success) {
        setMessages(prev => prev.map(msg => ({
          ...msg,
          actions: msg.actions?.map(a =>
            a.id === action.id ? { ...a, applied: true } : a,
          ),
        })));
        notify.success(`Applied: ${action.label}`);
      } else {
        throw new Error(data.error || 'Failed to apply action');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to apply action';
      notify.error(errorMessage);
    }
  }, [moduleContext]);

  // --- loadSession (extracted from 9 assistant components) ---
  const loadSession = useCallback(async (targetSessionId: string, sessionDate: string) => {
    setIsLoadingHistory(true);
    try {
      const tokens = authService.getTokens();
      if (!tokens?.access_token) return;

      const response = await fetch('/api/v1/ai-assistant/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tokens.access_token}`,
        },
        body: JSON.stringify({
          action: 'get_or_create',
          moduleContext,
          context: {
            sessionDate,
            ...(contextPayloadRef.current || {}),
          },
        }),
      });

      const data = await response.json();
      if (data.success) {
        setSessionId(data.session?.id || targetSessionId);
        const loadedMessages: AIMessage[] = Array.isArray(data.session?.messages)
          ? data.session.messages.map((msg: AIMessage, idx: number) => ({
              ...msg,
              id: msg.id || `loaded-msg-${idx}-${Date.now()}`,
            }))
          : [];
        setMessages(loadedMessages);
      }
    } catch (error) {
      console.error('[useAIAssistant] Failed to load session:', error);
      notify.error('Failed to load chat history');
    } finally {
      setIsLoadingHistory(false);
    }
  }, [moduleContext]);

  return {
    ...context,
    messages,
    setMessages,
    isGenerating,
    setIsGenerating,
    sessionId,
    isLoadingHistory,
    sendMessage,
    newChat,
    feedback,
    applyAction,
    loadSession,
    semanticContext: semanticContextHook.context || '',
    playbookContext: semanticContextHook.playbookContext || '',
    suggestedPrompts: semanticContextHook.suggestedPrompts || [],
    hasApiContext: semanticContextHook.hasApiContext,
  };
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Shift a hex color's hue by a specified number of degrees
 */
function shiftColor(hex: string, degrees: number): string {
  // Parse hex
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  } else {
    return hex; // Invalid, return original
  }
  
  // Convert to HSL
  r /= 255;
  g /= 255;
  b /= 255;
  
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
    }
  }
  
  // Shift hue
  h = (h + degrees / 360) % 1;
  if (h < 0) h += 1;
  
  // Convert back to RGB
  let r1: number, g1: number, b1: number;
  
  if (s === 0) {
    r1 = g1 = b1 = l;
  } else {
    const hue2rgb = (p: number, q: number, t: number) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r1 = hue2rgb(p, q, h + 1/3);
    g1 = hue2rgb(p, q, h);
    b1 = hue2rgb(p, q, h - 1/3);
  }
  
  // Convert back to hex
  const toHex = (x: number) => {
    const hex = Math.round(x * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  
  return `#${toHex(r1)}${toHex(g1)}${toHex(b1)}`;
}

export default AIAssistantContext;
